import { RIASSchemes } from "~/lib";

type NonDomThreshold = {
	lower: number;
	upper: number;
	unit: number;
	multiplier: number;
};

const rawSchemes = await import("~/data/schemes.json");
const schemes: RIASSchemes = rawSchemes;

export const certificateCost = (certificate: any) => {
	if (certificate.scheme === "section-6-domestic") {
		return domesticCost(certificate, schemes.s6Domestic);
	}

	if (certificate.scheme === "section-6-non-domestic") {
		return nonDomesticCost(certificate, schemes.s6NonDomestic);
	}

	return 1001;
};

const domesticCost = (certificate: any, fees) => {
	if (certificate.meta.amendments?.length > 0) {
		return fees.amendmentFee;
	}

	if (certificate.meta.stages?.length > 1) {
		return fees.stageFee;
	}

	let cost = 0;

	let plotCount = certificate.meta.plots?.length ?? 0;

	if (plotCount > 20) {
		// 21 to infinite
		cost += (plotCount - 20) * Math.round(fees.plotFee * 0.333); // £10 each
	}
	plotCount = Math.min(plotCount, 20);

	if (plotCount > 10) {
		// 11 to 20
		cost += (plotCount - 10) * Math.round(fees.plotFee * 0.666); // £20 each
	}
	plotCount = Math.min(plotCount, 10);

	if (plotCount > 5) {
		// 6 to 10
		cost += (plotCount - 5) * Math.round(fees.plotFee * 0.825); // £25 each
	}
	plotCount = Math.min(plotCount, 5);

	if (plotCount > 0) {
		// 1 to 5
		cost += plotCount * fees.plotFee; // £30 each
	}

	return cost;
};

const nonDomesticCost = (certificate: any, fees) => {
	if (certificate.meta.amendments?.length > 0) {
		return fees.amendmentFee;
	}

	if (certificate.meta.stages?.length > 1) {
		return fees.stageFee;
	}

	const warrantFee = getWarrantFee(certificate.meta.valueOfWorks);

	return Math.max(30, warrantFee * fees.certificateRate); // Make it a minimum of £30
};

export const getWarrantFee = (worksValue: number): number => {
	// Min works value fee for charge purposes is £5K
	worksValue = Math.max(worksValue, 5000);

	let warrantFee = 150;

	const thresholds: NonDomThreshold[] = [
		{ lower: 5e3, upper: 1e4, unit: 5e2, multiplier: 19 },
		{ lower: 1e4, upper: 2e4, unit: 1e3, multiplier: 19 },
		{ lower: 2e4, upper: 1e5, unit: 1e4, multiplier: 63 },
		{ lower: 1e5, upper: 5e5, unit: 2e4, multiplier: 103 },
		{ lower: 5e5, upper: 1e6, unit: 5e4, multiplier: 178 },
		{ lower: 1e6, upper: 1e11, unit: 1e5, multiplier: 253 },
	];

	for (let i = 0; i < thresholds.length; i++) {
		const t = thresholds[i];

		const additionalFee = costCalc(
			worksValue,
			t.lower,
			t.upper,
			t.unit,
			t.multiplier,
		);

		warrantFee += additionalFee;

		if (additionalFee <= 0) break;
	}

	return Math.round(warrantFee);
};

export const costCalc = (
	worksValue: number,
	lower: number,
	upper: number,
	unit: number,
	multiplier: number,
): number => {
	const value = Math.max(
		Math.min(upper, worksValue) - Math.min(lower, worksValue),
		0,
	);

	return multiplier * Math.ceil(value / unit);
};
